import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from "react";
import { Link, Outlet, useLocation } from "react-router";
import { Logo } from "./Logo";
import { SkeletonLoader } from "./SkeletonLoader";
import { LoginButton } from "./LoginButton";
import { UserDropdown } from "./UserDropdown";
import { useUser } from "../../lib/contexts/UserContextUtils";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, PuzzlePieceIcon } from "@heroicons/react/20/solid";
import { changeLanguage } from "../../i18n";
import { dweebPromoUrl } from "../../lib/Utils/dweeb";
// The registry the static generators build their nav from. It is plain data
// with no imports, so the SPA footer links the same answer pages they do.
import { ANSWER_PAGES } from "../../../scripts/answer-page-registry";

// --- New: Country Flag Icons ---
import { GB, ES, FR, BR, DE, SA, MY } from "country-flag-icons/react/3x2";

interface Language {
  code: string;
  name: string;
}

const languageToCountryCode: Record<string, string> = {
  en: "GB",
  es: "ES",
  fr: "FR",
  "pt-BR": "BR",
  de: "DE",
  ar: "SA", // Arabic
  ms: "MY", // Bahasa Melayu
};

const languages: Language[] = [
  { code: "en", name: "English" }, // 1. English
  { code: "es", name: "Español" }, // 2. Spanish
  { code: "fr", name: "Français" }, // 3. French
  { code: "pt-BR", name: "Português (Brasil)" }, // 4. Portuguese (Brazil)
  { code: "de", name: "Deutsch" }, // 5. German
  { code: "ar", name: "العربية" }, // 6. Arabic
  { code: "ms", name: "Bahasa Melayu" }, // 7. Malay
];

// --- Flag icon component (larger, aligned) ---
const FlagIcon = ({ countryCode }: { countryCode: string }) => {
  const flags: Record<string, JSX.Element> = {
    GB: <GB title="English" className="w-full h-full scale-150" />,
    ES: <ES title="Español" className="w-full h-full scale-150" />,
    FR: <FR title="Français" className="w-full h-full scale-150" />,
    BR: <BR title="Português (Brasil)" className="w-full h-full scale-150" />,
    DE: <DE title="Deutsch" className="w-full h-full scale-150" />,
    SA: <SA title="العربية" className="w-full h-full scale-150" />,
    MY: <MY title="Bahasa Melayu" className="w-full h-full scale-150" />,
  };

  return (
    <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
      {flags[countryCode] || flags.GB}
    </div>
  );
};

interface ListboxOption {
  code: string;
  name: string;
}

function useListboxKeyboardNav({
  isOpen,
  options,
  listRef,
  triggerRef,
  onSelect,
  onClose,
}: {
  isOpen: boolean;
  options: ListboxOption[];
  listRef: RefObject<HTMLUListElement | null>;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onSelect: (code: string) => void;
  onClose: () => void;
}) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex((idx) => Math.max(0, Math.min(idx, options.length - 1)));
  }, [isOpen, options.length]);

  useEffect(() => {
    if (!isOpen) return;
    const items = listRef.current?.querySelectorAll<HTMLLIElement>(
      "li[role='option']",
    );
    items?.[activeIndex]?.focus();
  }, [isOpen, activeIndex, listRef]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLUListElement>) => {
      if (!options.length) return;
      const last = options.length - 1;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((i) => (i >= last ? 0 : i + 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((i) => (i <= 0 ? last : i - 1));
          break;
        case "Home":
          e.preventDefault();
          setActiveIndex(0);
          break;
        case "End":
          e.preventDefault();
          setActiveIndex(last);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          onSelect(options[activeIndex].code);
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          triggerRef.current?.focus();
          break;
        default:
          if (e.key.length === 1 && /\S/.test(e.key)) {
            const letter = e.key.toLowerCase();
            const start = activeIndex + 1;
            for (let i = 0; i < options.length; i++) {
              const idx = (start + i) % options.length;
              if (options[idx].name.toLowerCase().startsWith(letter)) {
                e.preventDefault();
                setActiveIndex(idx);
                break;
              }
            }
          }
      }
    },
    [activeIndex, options, onSelect, onClose, triggerRef],
  );

  return { activeIndex, setActiveIndex, onKeyDown };
}

export default function HomeLayout() {
  const { user, initialized } = useUser();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const langTriggerRef = useRef<HTMLButtonElement>(null);
  const langListRef = useRef<HTMLUListElement>(null);

  const noPaddingRoutes = ["/"];
  const disablePadding = noPaddingRoutes.includes(location.pathname);

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];
  const currentFlagCode = languageToCountryCode[currentLanguage.code] || "GB";

  const handleLanguageChange = async (lng: string) => {
    await changeLanguage(lng);
    setIsLangMenuOpen(false);
    langTriggerRef.current?.focus();
  };

  const { activeIndex, setActiveIndex, onKeyDown: onListKeyDown } =
    useListboxKeyboardNav({
      isOpen: isLangMenuOpen,
      options: languages,
      listRef: langListRef,
      triggerRef: langTriggerRef,
      onSelect: handleLanguageChange,
      onClose: () => setIsLangMenuOpen(false),
    });

  useEffect(() => {
    if (!isLangMenuOpen) return;
    const idx = languages.findIndex((l) => l.code === i18n.language);
    if (idx >= 0) setActiveIndex(idx);
  }, [isLangMenuOpen, i18n.language, setActiveIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        langMenuRef.current &&
        !langMenuRef.current.contains(event.target as Node)
      ) {
        setIsLangMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-30 px-3 py-3 sm:px-4">
        <div className="max-w-7xl mx-auto flex h-14 items-center justify-between rounded-2xl border border-white/10 bg-gray-900/70 px-4 shadow-lg shadow-black/30 backdrop-blur-md sm:px-5">
          <Logo />

          <div className="flex items-center gap-4">
            <a
              href="/integrations/"
              className="hidden sm:inline-flex items-center gap-2 h-10 px-4 bg-white/5 hover:bg-white/10 border border-white/10 transition-colors rounded-lg text-gray-200 hover:text-white text-sm font-medium"
            >
              <PuzzlePieceIcon className="w-4 h-4" />
              {t("HomeLayout.plugins", "Integrations")}
            </a>
            {/* Language Selector */}
            <div className="relative" ref={langMenuRef}>
              <button
                ref={langTriggerRef}
                onClick={() => setIsLangMenuOpen((open) => !open)}
                onKeyDown={(e) => {
                  if (
                    e.key === "ArrowDown" ||
                    e.key === "Enter" ||
                    e.key === " "
                  ) {
                    e.preventDefault();
                    setIsLangMenuOpen(true);
                  }
                }}
                className="cursor-pointer h-10 flex items-center gap-3 justify-center bg-white/5 hover:bg-white/10 border border-white/10 transition-colors rounded-lg px-3 py-2 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
                aria-expanded={isLangMenuOpen}
                aria-haspopup="listbox"
                type="button"
              >
                <FlagIcon countryCode={currentFlagCode} />
                <span className="hidden sm:inline">
                  {currentLanguage.code.toUpperCase()}
                </span>
                <ChevronDownIcon className="w-4 h-4 ml-auto text-current [@media(max-width:400px)]:hidden" />
              </button>

              {isLangMenuOpen && (
                <ul
                  ref={langListRef}
                  role="listbox"
                  aria-activedescendant={
                    languages[activeIndex]?.code ?? currentLanguage.code
                  }
                  tabIndex={-1}
                  onKeyDown={onListKeyDown}
                  className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-30 overflow-hidden"
                >
                  {languages.map((lang, idx) => {
                    const selected = i18n.language === lang.code;
                    const flagCode = languageToCountryCode[lang.code] || "GB";
                    const isActive = idx === activeIndex;

                    return (
                      <li
                        key={lang.code}
                        id={lang.code}
                        role="option"
                        aria-selected={selected}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => handleLanguageChange(lang.code)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`flex items-center gap-3 px-4 py-2 cursor-pointer text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-inset ${
                          selected
                            ? "bg-gray-700 text-white"
                            : "text-gray-300 hover:bg-gray-700 hover:text-white"
                        }`}
                      >
                        <FlagIcon countryCode={flagCode} />
                        <span>{lang.name}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {!initialized ? (
              <SkeletonLoader width="w-24" height="h-10" />
            ) : !user ? (
              <LoginButton />
            ) : (
              <UserDropdown />
            )}
          </div>
        </div>
      </header>

      <main
        className={`flex-1 flex flex-col items-center space-y-10 ${
          disablePadding ? "" : "px-4 pt-24 pb-8 md:pt-28 md:pb-12 mb-10"
        }`}
      >
        <Outlet />
      </main>

      <footer className="w-full py-6 px-4 border-t border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        {/* Answer pages are English-only, so their registry labels are used
            as-is. The label is a <p>, not a heading, as in the static footers;
            the nav's aria-label carries it for assistive tech. */}
        <div className="max-w-7xl mx-auto mb-5 pb-5 border-b border-gray-800 text-gray-400 text-sm flex flex-col sm:flex-row items-center sm:items-baseline gap-2 sm:gap-4">
          <p className="shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-300">
            {t("HomeLayout.guides", "Guides")}
          </p>
          <nav
            aria-label={t("HomeLayout.guides", "Guides")}
            className="flex flex-wrap justify-center sm:justify-start gap-x-5 gap-y-2"
          >
            {ANSWER_PAGES.map((page) => (
              <a
                key={page.slug}
                href={`/${page.slug}`}
                className="hover:text-white transition-colors"
              >
                {page.navLabel}
              </a>
            ))}
          </nav>
        </div>
        <div className="max-w-7xl mx-auto text-gray-400 text-sm flex flex-col sm:flex-row justify-between items-center gap-4">
          <p>{t("HomeLayout.footer_text")}</p>
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 sm:gap-6">
            <Link to="/terms" className="hover:text-white transition-colors">
              {t("HomeLayout.terms")}
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors">
              {t("HomeLayout.privacy")}
            </Link>
            <Link to="/status" className="hover:text-white transition-colors">
              {t("HomeLayout.status", "Status")}
            </Link>
            <a
              href="/integrations/"
              className="hover:text-white transition-colors"
            >
              {t("HomeLayout.plugins", "Integrations")}
            </a>
            <a
              href={import.meta.env.VITE_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              {t("HomeLayout.docs")}
            </a>
            <a
              href={dweebPromoUrl("footer")}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              {t("HomeLayout.dweeb", "DWEEB · Webhook Builder")}
            </a>
          </div>
        </div>
      </footer>
    </>
  );
}
